/**
 * Auth Store
 * Zustand store com persist para autenticação
 */

import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";
import apiClient from "@/lib/api-client";
import type { AuthState, User } from "./use-auth.types";

interface AuthStore extends AuthState {
    setUser: (user: User) => void;
    clearAuth: () => void;
    setLoading: (loading: boolean) => void;
    initializeAuth: () => Promise<void>;
}

/**
 * Store de autenticação
 * - Token JWT de 7 dias (sem refresh)
 * - Persistido em localStorage (bacbo_auth_token)
 * - Exposto globalmente em window.__ZUSTAND_AUTH_STORE__
 */
export const useAuthStore = create<AuthStore>()(
    devtools(
        persist(
            (set) => ({
                // Estado inicial
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: true,

                // Setar usuário
                setUser: (user: User) => {
                    set({
                        user,
                        isAuthenticated: true,
                        isLoading: false,
                    });
                },

                // Limpar autenticação
                clearAuth: () => {
                    // Limpar localStorage
                    if (typeof window !== "undefined") {
                        localStorage.removeItem("bacbo_auth_token");
                        localStorage.removeItem("bacbo_user");
                    }

                    // Limpar state
                    set({
                        user: null,
                        token: null,
                        isAuthenticated: false,
                        isLoading: false,
                    });
                }, // Setar loading
                setLoading: (loading: boolean) => {
                    set({ isLoading: loading });
                },

                // Inicializar autenticação (verificar token existente)
                initializeAuth: async () => {
                    const token =
                        typeof window !== "undefined"
                            ? localStorage.getItem("bacbo_auth_token")
                            : null;

                    if (!token) {
                        set({ isLoading: false });
                        return;
                    }

                    try {
                        const response = await apiClient.get("/api/auth/me");

                        if (response.data.success && response.data.user) {
                            set({
                                user: response.data.user,
                                isAuthenticated: true,
                                isLoading: false,
                            });
                            return;
                        }

                        // Token inválido ou erro - limpar tudo
                        if (typeof window !== "undefined") {
                            localStorage.removeItem("bacbo_auth_token");
                            localStorage.removeItem("bacbo_user");
                        }
                        set({
                            user: null,
                            token: null,
                            isAuthenticated: false,
                            isLoading: false,
                        });
                    } catch (error) {
                        console.error("Erro ao inicializar autenticação:", error);
                        if (typeof window !== "undefined") {
                            localStorage.removeItem("bacbo_auth_token");
                            localStorage.removeItem("bacbo_user");
                        }
                        set({
                            user: null,
                            token: null,
                            isAuthenticated: false,
                            isLoading: false,
                        });
                    }
                },
            }),
            {
                name: "bacbo_auth_user",
                partialize: (state) => ({
                    user: state.user,
                }),
            }
        ),
        {
            name: "AuthStore",
        }
    )
);

// Expor store globalmente para uso em funções não-React
if (typeof window !== "undefined") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__ZUSTAND_AUTH_STORE__ = useAuthStore;
}
